// RedesSociais.js
// O embed do Instagram e pesado (script externo + iframe + fontes + imagens).
// Antes ele era injetado assim que a pagina montava, disputando banda com o
// resto do site e deixando a secao em branco por varios segundos.
//
// Agora:
//  - o script so entra quando a secao esta chegando na tela (IntersectionObserver)
//  - ate o iframe existir, mostramos um esqueleto do tamanho certo, entao nao
//    ha "pulo" de layout nem area vazia sem explicacao
//  - o script e carregado uma unica vez por pagina e reaproveitado

import React, { useEffect, useRef, useState } from 'react';
import {
  RedesContainer, SectionTitle, Subtitle, EmbedContainer, InteractiveText,
  Placeholder, PlaceholderTexto,
} from './RedesSociaisStyle';

const SRC = 'https://www.instagram.com/embed.js';
const POST = 'https://www.instagram.com/p/DUsj4j-jsfm/';

/** Carrega o embed.js uma vez so, mesmo se a secao remontar. */
function carregarScript() {
  if (window.instgrm) {
    window.instgrm.Embeds.process();
    return;
  }
  if (document.querySelector(`script[src="${SRC}"]`)) return;

  const script = document.createElement('script');
  script.src = SRC;
  script.async = true;
  document.body.appendChild(script);
}

const RedesSociais = () => {
  const containerRef = useRef(null);
  const [pronto, setPronto] = useState(false);

  // Busca o script quando a seção se aproxima da viewport — com rede de
  // segurança. Sem ela, qualquer situação em que o IntersectionObserver não
  // dispara (viewport de altura zero, navegador antigo, aba oculta) deixaria
  // o post preso no esqueleto para sempre. Melhor atrasar do que nunca abrir.
  useEffect(() => {
    const alvo = containerRef.current;
    if (!alvo) return undefined;

    let jaPediu = false;
    let observador;
    let timer;

    const limpar = () => {
      observador?.disconnect();
      clearTimeout(timer);
    };

    const pedir = () => {
      if (jaPediu) return;
      jaPediu = true;
      carregarScript();
      limpar();
    };

    if (typeof IntersectionObserver === 'function') {
      observador = new IntersectionObserver((entradas) => {
        if (entradas[0]?.isIntersecting) pedir();
      }, { rootMargin: '400px' });
      observador.observe(alvo);
    } else {
      pedir();
    }

    timer = setTimeout(pedir, 2500);
    return limpar;
  }, []);

  // O Instagram troca o <blockquote> por um <iframe>; é esse o sinal de pronto.
  useEffect(() => {
    const alvo = containerRef.current;
    if (!alvo) return undefined;

    if (alvo.querySelector('iframe')) {
      setPronto(true);
      return undefined;
    }

    const observador = new MutationObserver(() => {
      if (alvo.querySelector('iframe')) {
        setPronto(true);
        observador.disconnect();
      }
    });

    observador.observe(alvo, { childList: true, subtree: true });
    return () => observador.disconnect();
  }, []);

  return (
    <RedesContainer>
      <SectionTitle>Redes Sociais</SectionTitle>
      <Subtitle>
        Me siga no <span>Instagram</span> e fique conectado!
      </Subtitle>
      <InteractiveText>post interativo abaixo, clique ou arraste</InteractiveText>
      <EmbedContainer ref={containerRef} $pronto={pronto}>
        {!pronto && (
          <Placeholder aria-hidden="true">
            <PlaceholderTexto>carregando o post...</PlaceholderTexto>
          </Placeholder>
        )}
        <blockquote
          className="instagram-media"
          data-instgrm-permalink={POST}
          data-instgrm-version="14"
          style={{ maxWidth: '540px', width: '100%' }}
        />
      </EmbedContainer>
    </RedesContainer>
  );
};

export default RedesSociais;
